import { trpc } from '@/trpcClient';
import type { FlowSensorDto } from '@shelflix/trpc/routes/flowSensor/list/useCase';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shelflix/ui-web/components/ui/dialog';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Label } from '@shelflix/ui-web/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const MAC_RE = /^([0-9A-Fa-f]{2}:?){5}[0-9A-Fa-f]{2}$/;
const NO_FACILITY = 'none';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensor?: FlowSensorDto | null; // edição
  initialMac?: string; // cadastro a partir de um sensor encontrado
  initialProduct?: string | null;
  onSaved: () => void;
}

export function SensorFormDialog({ open, onOpenChange, sensor, initialMac, initialProduct, onSaved }: Props) {
  const [mac, setMac] = useState('');
  const [name, setName] = useState('');
  const [product, setProduct] = useState('');
  const [facilityUuid, setFacilityUuid] = useState(NO_FACILITY);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setMac(sensor?.mac ?? initialMac ?? '');
    setName(sensor?.name ?? '');
    setProduct(sensor?.product ?? initialProduct ?? '');
    setFacilityUuid(sensor?.facility?.uuid ?? NO_FACILITY);
    setNotes(sensor?.notes ?? '');
  }, [open, sensor, initialMac, initialProduct]);

  const { data: facilities } = useQuery({
    ...trpc.facility.list.queryOptions({ page: 1, recordsPerPage: 9999 }),
    enabled: open,
  });

  const register = useMutation(trpc.flowSensor.register.mutationOptions());
  const edit = useMutation(trpc.flowSensor.edit.mutationOptions());
  const saving = register.isPending || edit.isPending;

  const macError = !sensor && mac && !MAC_RE.test(mac.trim()) ? 'Use os 12 caracteres do MAC (ex.: 3C8427C6A964)' : '';
  const canSave = name.trim().length > 0 && (sensor || (mac && !macError));

  const save = () => {
    const fields = {
      name: name.trim(),
      product: product.trim(),
      notes: notes.trim(),
      facilityUuid: facilityUuid === NO_FACILITY ? null : facilityUuid,
    };
    const callbacks = {
      onSuccess: () => {
        toast.success(sensor ? 'Sensor atualizado' : 'Sensor cadastrado');
        onOpenChange(false);
        onSaved();
      },
      onError: (error: { message: string }) => toast.error(error.message),
    };
    if (sensor) edit.mutate({ uuid: sensor.uuid, ...fields }, callbacks);
    else register.mutate({ mac: mac.trim(), ...fields }, callbacks);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{sensor ? 'Editar sensor de fluxo' : 'Cadastrar sensor de fluxo'}</DialogTitle>
          <DialogDescription>
            Cadastre o receptor do par. O MAC aparece no Wi-Fi do sensor (Fluxo-XXXXXX são os 6 últimos caracteres) e na
            página do sensor.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="fs-mac">MAC do receptor</Label>
            <Input
              id="fs-mac"
              value={mac}
              disabled={!!sensor || !!initialMac}
              placeholder="3C8427C6A964"
              onChange={(e) => setMac(e.target.value.toUpperCase())}
            />
            {macError && <span className="text-xs text-red-500">{macError}</span>}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fs-name">Nome</Label>
            <Input
              id="fs-name"
              value={name}
              maxLength={80}
              placeholder="Ex.: Gôndola café – loja Centro"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fs-product">Produto monitorado</Label>
            <Input
              id="fs-product"
              value={product}
              maxLength={80}
              placeholder="Ex.: Café torrado 500 g"
              onChange={(e) => setProduct(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Estabelecimento</Label>
            <Select value={facilityUuid} onValueChange={setFacilityUuid}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_FACILITY}>Sem estabelecimento</SelectItem>
                {facilities?.facilities.map((f) => (
                  <SelectItem key={f.uuid} value={f.uuid}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fs-notes">Observações</Label>
            <Textarea id="fs-notes" value={notes} maxLength={255} rows={2} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={save} disabled={!canSave || saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
